import { useTranslation } from 'react-i18next';
import { AppContext } from "../../context/context";
import Box from '@mui/material/Box';
import { ButtonBase, Stack, Tooltip, Typography } from "@mui/material";

import { useAddLayer } from "./functions/layerFunctions";
import { useContext, useEffect, type ReactNode } from "react";
import { trackAnalyticsEvent } from "../../services/analytics";
import { ANALYTICS_EVENT_NAMES } from "../../services/eventTaxonomy";
import type { MapToolActivationSource, MapToolName } from "../../services/eventTypes";
import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
import { opsColors } from '../../theme/opsTokens';
interface MapToolBarProps {
    onImportClick: () => void;
    hideAddLayerButton?: boolean;
}

type ToolTileProps = {
    label: string;
    tooltip: string;
    ariaLabel: string;
    icon: ReactNode;
    active?: boolean;
    onClick: () => void;
    testId?: string;
};

function ToolTile({ label, tooltip, ariaLabel, icon, active = false, onClick, testId }: ToolTileProps) {
    const tileStyles = {
        width: 50,
        minHeight: 50,
        borderRadius: 1.25,
        border: `1px solid ${active ? opsColors.brand.primary : opsColors.neutral.line}`,
        backgroundColor: active ? 'rgba(244, 122, 53, 0.08)' : opsColors.neutral.panel,
        color: active ? opsColors.brand.primary : 'text.secondary',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.2,
        px: 0.3,
        py: 0.35,
        transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease',
        '&:hover': {
            backgroundColor: active ? 'rgba(244, 122, 53, 0.12)' : opsColors.neutral.panelAlt,
            transform: 'translateY(-1px)',
        },
    } as const;

    return (
        <Tooltip title={tooltip}>
            <ButtonBase
                aria-label={ariaLabel}
                onClick={onClick}
                sx={tileStyles}
                data-testid={testId}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 18 }}>
                    {icon}
                </Box>
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: '0.56rem',
                        lineHeight: 1,
                        fontWeight: 600,
                        textAlign: 'center',
                        color: 'inherit',
                        whiteSpace: 'normal',
                    }}
                >
                    {label}
                </Typography>
            </ButtonBase>
        </Tooltip>
    );
}

export default function MapToolBar({ onImportClick, hideAddLayerButton = false }: MapToolBarProps) {
    const { t } = useTranslation('map');
    const context = useContext(AppContext);

    if (!context) {
        throw new Error("MapToolBar must be used within the context provider")
    }

    const {modeSettings, setModeSettings} = context;
    const {mode} = modeSettings;

    const addLayer = useAddLayer();

    const MODE_TO_TOOL_NAME: Record<string, MapToolName> = {
        'Select': 'select',
        'Add Points': 'add_points',
        'Draw Polygon': 'draw_polygon',
        'Draw Line': 'draw_line',
    };

    function trackToolActivation(toolName: MapToolName, source: MapToolActivationSource, isActive: boolean) {
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.map_tool_activated, {
            tool_name: toolName,
            activation_source: source,
            is_active: isActive,
            timestamp: new Date().toISOString(),
        });
    }

    function handleIconClick(funcName: string, source: MapToolActivationSource = 'click') {
        const newMode = mode === funcName ? "" : funcName
        const toolName = MODE_TO_TOOL_NAME[funcName];
        if (toolName) {
            trackToolActivation(toolName, source, newMode === funcName);
        }
        setModeSettings(prev => {
            return {
                ...prev,
                mode: newMode,
                // Reset drawing points when switching modes
                ...(newMode !== 'Draw Polygon' ? { drawingPolygonPoints: [] } : {}),
                ...(newMode !== 'Draw Line' ? { drawingLinePoints: [] } : {})
            }
        })
    }

    const handleAddLayer = (source: MapToolActivationSource = 'click') => {
        trackToolActivation('add_layer', source, true);
        addLayer();
    };

    const handleImportClick = () => {
        trackToolActivation('import', 'click', true);
        onImportClick();
    };

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            // Only trigger if no input elements are focused
            if (document.activeElement?.tagName === 'INPUT' || 
                document.activeElement?.tagName === 'TEXTAREA') {
                return;
            }

            // Prevent triggering when modifier keys are pressed
            if (e.ctrlKey || e.altKey || e.metaKey) {
                return;
            }

            // Guard: e.key may be undefined/null in some edge cases
            if (!e.key || typeof e.key !== 'string') {
                return;
            }

            switch (e.key.toLowerCase()) {
                case 's':
                    handleIconClick('Select', 'keyboard');
                    break;
                case 'p':
                    handleIconClick('Add Points', 'keyboard');
                    break;
                case 'g':
                    handleIconClick('Draw Polygon', 'keyboard');
                    break;
                case 'l':
                    handleIconClick('Draw Line', 'keyboard');
                    break;
                case 'n':
                    handleAddLayer('keyboard');
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, addLayer]);

    return (
        <Box
            sx={{
                px: 0.25,
            }}
        >
            <Stack
                direction="row"
                spacing={0.4}
                alignItems="flex-start"
                justifyContent="center"
                flexWrap="nowrap"
                useFlexGap
                sx={{ overflowX: 'hidden' }}
            >
                {!hideAddLayerButton && (
                    <ToolTile
                        label="Layer"
                        tooltip={t('tools.addLayer')}
                        ariaLabel={t('tools.addLayerAria')}
                        icon={<Icon size={16} name="circle-plus" />}
                        onClick={() => handleAddLayer('click')}
                        testId="map.toolbar.add-layer"
                    />
                )}

                <ToolTile
                    label="Select"
                    tooltip={t('tools.select')}
                    ariaLabel={t('tools.selectAria')}
                    active={mode === 'Select'}
                    icon={<Icon size={16} d={ICON_FALLBACKS.PanToolOutlined} />}
                    onClick={() => { handleIconClick('Select'); }}
                    testId="map.toolbar.select"
                />

                <ToolTile
                    label="Point"
                    tooltip={t('tools.addPoints')}
                    ariaLabel={t('tools.addPointsAria')}
                    active={mode === 'Add Points'}
                    icon={<Icon size={16} d={ICON_FALLBACKS.AddLocationAltOutlined} />}
                    onClick={() => { handleIconClick('Add Points'); }}
                    testId="map.toolbar.add-points"
                />

                <ToolTile
                    label="Area"
                    tooltip={t('tools.drawPolygon')}
                    ariaLabel={t('tools.drawPolygonAria')}
                    active={mode === 'Draw Polygon'}
                    icon={<Icon size={16} d={ICON_FALLBACKS.PentagonOutlined} />}
                    onClick={() => { handleIconClick('Draw Polygon'); }}
                    testId="map.toolbar.draw-polygon"
                />

                <ToolTile
                    label="Line"
                    tooltip={t('tools.drawLine')}
                    ariaLabel={t('tools.drawLineAria')}
                    active={mode === 'Draw Line'}
                    icon={<Icon size={16} d={ICON_FALLBACKS.ShowChartOutlined} />}
                    onClick={() => handleIconClick('Draw Line', 'menu')}
                    testId="map.toolbar.draw-line"
                />

                <ToolTile
                    label="Import"
                    tooltip={t('tools.importTooltip')}
                    ariaLabel={t('tools.importAria')}
                    icon={<Icon size={16} d={ICON_FALLBACKS.InputOutlined} />}
                    onClick={() => handleImportClick()}
                    testId="map.toolbar.import"
                />
            </Stack>
        </Box>
    )
}
